// GM Tenant Switching Feature - TypeScript Interfaces
// Frontend team can use these interfaces for type safety and API integration

import { Tenant, User, Role } from "./task.interface";




// ============================================================================
// ENUMS
// ============================================================================

export enum AssignmentType {
    SUPPORT = 'support',
    BACKUP = 'backup',
    TEMPORARY = 'temporary',
    PERMANENT = 'permanent'
  }
  
  export enum AssignmentStatus {
    ACTIVE = 'active',
    INACTIVE = 'inactive',
    EXPIRED = 'expired'
  }
  
  
  
  // ============================================================================
  // GM TENANT ASSIGNMENT INTERFACES
  // ============================================================================
  
  export interface GMTenantAssignment {
    _id: string;
    gmUser: User;
    assignedTenant: Tenant;
    assignedBy: User;
    assignmentType: AssignmentType;
    reason: string;
    startDate: string; // ISO date string
    endDate?: string; // ISO date string, optional
    isActive: boolean;
    notes?: string;
    createdAt: string; // ISO date string
    updatedAt: string; // ISO date string
  }
  
  export interface GMTenantAssignmentWithDetails extends GMTenantAssignment {
    // Additional populated fields
    gmUser: User & {
      roles: Role[];
      tenant: Tenant;
    };
    assignedTenant: Tenant & {
      settings?: any; // Tenant-specific settings
    };
    assignedBy: User & {
      roles: Role[];
    };
  }
  
  // ============================================================================
  // REQUEST INTERFACES
  // ============================================================================
  
  // POST /api/gm-tenant-assignments/assign
  export interface AssignGMToTenantRequest {
    gmUserId: string;
    tenantId: string;
    reason: string;
    assignmentType?: AssignmentType;
    notes?: string;
    endDate?: string; // ISO date string, optional
  }
  
  // PATCH /api/gm-tenant-assignments/deactivate/:assignmentId
  export interface DeactivateAssignmentRequest {
    // No body required - uses URL parameter
  }
  
  // POST /api/gm-tenant-assignments/switch
  export interface SwitchToTenantRequest {
    tenantId: string;
  }
  
  // ============================================================================
  // RESPONSE INTERFACES
  // ============================================================================
  
  // Generic API Response Wrapper
  export interface ApiResponse<T = any> {
    success: boolean;
    message: string;
    data?: T;
    error?: string;
    timestamp: string;
  }
  
  // POST /api/gm-tenant-assignments/assign
  export interface AssignGMToTenantResponse extends ApiResponse {
    data: {
      assignment: GMTenantAssignmentWithDetails;
      message: string;
    };
  }
  
  // GET /api/gm-tenant-assignments/tenant/:tenantId
  export interface GetTenantAssignmentsResponse extends ApiResponse {
    data: {
      assignments: GMTenantAssignmentWithDetails[];
      total: number;
      activeCount: number;
      inactiveCount: number;
    };
  }
  
  // PATCH /api/gm-tenant-assignments/deactivate/:assignmentId
  export interface DeactivateAssignmentResponse extends ApiResponse {
    data: {
      assignment: GMTenantAssignmentWithDetails;
      message: string;
    };
  }
  
 // GET /api/gm-tenant-assignments/my-assignments
export interface GetMyAssignmentsResponse extends ApiResponse {
    data: {
      gmUser: {
        _id: string;
        name: string;
        email: string;
        isActive: boolean;
      };
      assignments: (GMTenantAssignmentWithDetails & {
        isPrimaryTenant?: boolean;
        isCurrentlyActive?: boolean;
      })[];
      total: number;
      activeCount: number;
      inactiveCount: number;
      primaryTenant: Tenant;
      currentActiveTenant: string;
      workload: {
        totalActive: number;
        totalInactive: number;
        additionalActive: number;
        byType: Record<AssignmentType | 'primary', number>;
        isOverloaded: boolean;
      };
    };
  }
  
  // POST /api/gm-tenant-assignments/switch
  export interface SwitchToTenantResponse extends ApiResponse {
    data: {
      newToken: string;
      activeTenant: Tenant;
      user: User & {
        roles: Role[];
        tenant: Tenant;
      };
      message: string;
    };
  }
  
  // GET /api/gm-tenant-assignments/available-tenants
  export interface GetAvailableTenantsResponse extends ApiResponse {
    data: {
      tenants: Tenant[];
      total: number;
      currentActiveTenant?: Tenant;
    };
  }
  
  // ============================================================================
  // ERROR INTERFACES
  // ============================================================================
  
  export interface ValidationError {
    field: string;
    message: string;
    value?: any;
  }
  
  export interface ApiErrorResponse extends ApiResponse {
    success: false;
    error: string;
    validationErrors?: ValidationError[];
    code?: string;
  }
  
  // ============================================================================
  // UTILITY INTERFACES
  // ============================================================================
  
  export interface PaginationParams {
    page?: number;
    limit?: number;
  }
  
  export interface PaginatedResponse<T> {
    data: T[];
    total: number;
    totalPages: number;
    currentPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  }
  
  export interface FilterParams {
    assignmentType?: AssignmentType;
    isActive?: boolean;
    startDate?: string;
    endDate?: string;
    search?: string;
  }
  
  // ============================================================================
  // FRONTEND STATE INTERFACES
  // ============================================================================
  
  export interface GMTenantAssignmentState {
    assignments: GMTenantAssignmentWithDetails[];
    availableTenants: Tenant[];
    currentActiveTenant?: Tenant;
    loading: boolean;
    error?: string;
    totalAssignments: number;
    activeAssignments: number;
  }
  
  export interface TenantSwitchState {
    isSwitching: boolean;
    error?: string;
    success?: boolean;
  }
  
  // ============================================================================
  // SOCKET EVENT INTERFACES
  // ============================================================================
  
  export interface TenantSwitchEvent {
    type: 'tenant_switch';
    userId: string;
    oldTenantId: string;
    newTenantId: string;
    timestamp: string;
  }
  
  export interface AssignmentUpdateEvent {
    type: 'assignment_update';
    assignmentId: string;
    action: 'created' | 'updated' | 'deactivated';
    assignment: GMTenantAssignmentWithDetails;
    timestamp: string;
  }
  
  // ============================================================================
  // CONSTANTS
  // ============================================================================
  
  export const GM_TENANT_ASSIGNMENT_ENDPOINTS = {
    ASSIGN: '/api/gm-tenant-assignments/assign',
    GET_TENANT_ASSIGNMENTS: '/api/gm-tenant-assignments/tenant',
    DEACTIVATE: '/api/gm-tenant-assignments/deactivate',
    GET_MY_ASSIGNMENTS: '/api/gm-tenant-assignments/my-assignments',
    SWITCH_TENANT: '/api/gm-tenant-assignments/switch',
    GET_AVAILABLE_TENANTS: '/api/gm-tenant-assignments/available-tenants',
    AM_GET_MY_ASSIGNED_TENANTS: '/api/gm-tenant-assignments/am/my-assigned-tenants',
    AM_GET_AVAILABLE_GMS: '/api/gm-tenant-assignments/am/available-gms',
    AM_GET_MANAGED_ASSIGNMENTS: '/api/gm-tenant-assignments/am/managed-assignments',
    AM_GET_ASSIGNMENT_SUMMARY: '/api/gm-tenant-assignments/am/assignment-summary',
    AM_GET_TENANT_GMS: '/api/gm-tenant-assignments/am/tenant',
    AM_GET_GM_ASSIGNMENTS: '/api/gm-tenant-assignments/am/gm',
    AM_GET_MARKET_AREAS: '/api/gm-tenant-assignments/am/market-areas'
  } as const;
  
  
  export const ASSIGNMENT_TYPE_LABELS: Record<AssignmentType, string> = {
    [AssignmentType.SUPPORT]: 'Support',
    [AssignmentType.BACKUP]: 'Backup',
    [AssignmentType.TEMPORARY]: 'Temporary',
    [AssignmentType.PERMANENT]: 'Permanent'
  };
  
  export const ASSIGNMENT_TYPE_DESCRIPTIONS: Record<AssignmentType, string> = {
    [AssignmentType.SUPPORT]: 'General support for the tenant',
    [AssignmentType.BACKUP]: 'Backup coverage when primary GM is unavailable',
    [AssignmentType.TEMPORARY]: 'Temporary assignment for specific period',
    [AssignmentType.PERMANENT]: 'Permanent assignment to the tenant'
  };
  
  // ============================================================================
  // HELPER TYPES
  // ============================================================================
  
  export type AssignmentTypeKey = keyof typeof AssignmentType;
  export type AssignmentStatusKey = keyof typeof AssignmentStatus;
  
  // Type for creating new assignments
  export type CreateAssignmentData = Omit<GMTenantAssignment, '_id' | 'createdAt' | 'updatedAt'>;
  
  // Type for updating assignments
  export type UpdateAssignmentData = Partial<Pick<GMTenantAssignment, 'isActive' | 'notes' | 'endDate'>>;
  
  // Type for assignment filters
  export type AssignmentFilters = FilterParams & PaginationParams;
  
  // ============================================================================
  // EXAMPLE USAGE
  // ============================================================================
  
  /*
  // Example usage in frontend components:
  
  // 1. Assign GM to tenant
  const assignGMToTenant = async (data: AssignGMToTenantRequest): Promise<AssignGMToTenantResponse> => {
    const response = await fetch(GM_TENANT_ASSIGNMENT_ENDPOINTS.ASSIGN, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return response.json();
  };
  
  // 2. Switch to different tenant
  const switchToTenant = async (data: SwitchToTenantRequest): Promise<SwitchToTenantResponse> => {
    const response = await fetch(GM_TENANT_ASSIGNMENT_ENDPOINTS.SWITCH_TENANT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return response.json();
  };
  
  // 3. Get available tenants
  const getAvailableTenants = async (): Promise<GetAvailableTenantsResponse> => {
    const response = await fetch(GM_TENANT_ASSIGNMENT_ENDPOINTS.GET_AVAILABLE_TENANTS);
    return response.json();
  };
  
  // 4. React component example
  interface GMTenantSwitcherProps {
    onTenantSwitch: (tenant: BaseTenant) => void;
    currentTenant?: BaseTenant;
  }
  
  const GMTenantSwitcher: React.FC<GMTenantSwitcherProps> = ({ onTenantSwitch, currentTenant }) => {
    const [availableTenants, setAvailableTenants] = useState<BaseTenant[]>([]);
    const [loading, setLoading] = useState(false);
  
    const handleTenantSwitch = async (tenantId: string) => {
      try {
        setLoading(true);
        const response = await switchToTenant({ tenantId });
        if (response.success) {
          onTenantSwitch(response.data.activeTenant);
          // Update auth token
          localStorage.setItem('token', response.data.newToken);
        }
      } catch (error) {
        console.error('Failed to switch tenant:', error);
      } finally {
        setLoading(false);
      }
    };
  
    return (
      <div>
        <select 
          value={currentTenant?._id || ''} 
          onChange={(e) => handleTenantSwitch(e.target.value)}
          disabled={loading}
        >
          {availableTenants.map(tenant => (
            <option key={tenant._id} value={tenant._id}>
              {tenant.name}
            </option>
          ))}
        </select>
      </div>
    );
  };
  */ 

  // ============================================================================
// MARKET AREA INTERFACES
// ============================================================================

export interface MarketAreaShop {
    shop: Tenant;
    currentGM: User;
    assignedDate: string;
  }
  
  export interface MarketArea {
    id: string;
    name: string;
    region?: string;
    corporateTenant: Tenant;
    shopCount: number;
    shops: MarketAreaShop[];
  }
  
  // ============================================================================
  // AREA MANAGER REQUEST INTERFACES
  // ============================================================================
  
  // GET /api/gm-tenant-assignments/am/market-areas
  export interface GetMyMarketAreasRequest {
    // No parameters required
  }
  
  // GET /api/gm-tenant-assignments/am/my-assigned-tenants
  export interface GetMyAssignedTenantsRequest {
    page?: number;
    limit?: number;
    includeInactive?: boolean;
  }
  
  // GET /api/gm-tenant-assignments/am/available-gms
  export interface GetAvailableGMsRequest {
    page?: number;
    limit?: number;
    search?: string;
    includeInactive?: boolean;
  }
  
  // GET /api/gm-tenant-assignments/am/managed-assignments
  export interface GetMyManagedAssignmentsRequest {
    page?: number;
    limit?: number;
    status?: 'active' | 'inactive' | 'expired' | 'all';
    assignmentType?: AssignmentType;
    search?: string;
  }
  
  // GET /api/gm-tenant-assignments/am/assignment-summary
  export interface GetAssignmentSummaryRequest {
    // No parameters required
  }
  
  // GET /api/gm-tenant-assignments/am/tenant/:tenantId/gms
  export interface GetTenantGMsRequest {
    includeInactive?: boolean;
  }
  
  // GET /api/gm-tenant-assignments/am/gm/:gmUserId/assignments
  export interface GetGMAssignmentsRequest {
    includeInactive?: boolean;
  }
  
  // ============================================================================
  // AREA MANAGER RESPONSE INTERFACES
  // ============================================================================
  
  // GET /api/gm-tenant-assignments/am/market-areas
  export interface GetMyMarketAreasResponse extends ApiResponse {
    data: {
      marketAreas: MarketArea[];
      totalMarketAreas: number;
      totalShops: number;
    };
  }
  
  // GET /api/gm-tenant-assignments/am/my-assigned-tenants
  export interface GetMyAssignedTenantsResponse extends ApiResponse {
    data: {
      tenants: (Tenant & {
        marketAreaId: string;
        marketAreaName: string;
        currentGM?: User;
      })[];
      total: number;
      totalPages: number;
      currentPage: number;
      assignedTenantCount: number;
    };
  }
  
  // GET /api/gm-tenant-assignments/am/available-gms
  export interface GetAvailableGMsResponse extends ApiResponse {
    data: {
      gms: (User & {
        roles: Role[];
        tenant: Tenant;
      })[];
      total: number;
      totalPages: number;
      currentPage: number;
      activeGMCount: number;
    };
  }
  
  // GET /api/gm-tenant-assignments/am/managed-assignments
  export interface GetMyManagedAssignmentsResponse extends ApiResponse {
    data: {
      assignments: GMTenantAssignmentWithDetails[];
      total: number;
      totalPages: number;
      currentPage: number;
      activeAssignments: number;
      inactiveAssignments: number;
      expiredAssignments: number;
    };
  }
  
  // GET /api/gm-tenant-assignments/am/assignment-summary
  export interface GetAssignmentSummaryResponse extends ApiResponse {
    data: {
      totalAssignments: number;
      activeAssignments: number;
      inactiveAssignments: number;
      expiredAssignments: number;
      totalTenants: number;
      totalGMs: number;
      assignmentsByType: Record<AssignmentType, number>;
      recentActivity: Array<{
        id: string;
        action: 'assigned' | 'deactivated';
        gmName: string;
        tenantName: string;
        date: string;
        type: AssignmentType;
      }>;
    };
  }
  
  // GET /api/gm-tenant-assignments/am/tenant/:tenantId/gms
  export interface GetTenantGMsResponse extends ApiResponse {
    data: {
      tenantId: string;
      gms: (User & {
        roles: Role[];
        assignmentId: string;
        assignmentType: AssignmentType;
        startDate: string;
        endDate?: string;
        reason: string;
        assignedBy: User;
      })[];
      total: number;
      activeCount: number;
      inactiveCount: number;
    };
  }
  
 // GET /api/gm-tenant-assignments/am/gm/:gmUserId/assignments
export interface GetGMAssignmentsResponse extends ApiResponse {
    data: {
      gmUserId: string;
      gmUser: {
        _id: string;
        name: string;
        email: string;
        isActive: boolean;
      };
      assignments: (GMTenantAssignmentWithDetails & {
        isPrimaryTenant?: boolean;
      })[];
      total: number;
      activeCount: number;
      inactiveCount: number;
      primaryTenant: Tenant;
      workload: {
        totalActive: number;
        totalInactive: number;
        additionalActive: number;
        byType: Record<AssignmentType | 'primary', number>;
        isOverloaded: boolean;
      };
    };
  } 


  